/**
 * Centralized Money & Currency Minor Unit Helpers
 * 
 * All monetary amounts in Villa Management OS are strictly stored and calculated
 * in INTEGER minor units (e.g. IDR: 150000 = Rp150,000; USD: 1234 = $12.34; MYR: 2550 = RM25.50).
 */

const CURRENCY_DECIMALS: Record<string, number> = {
  IDR: 0,
  USD: 2,
  MYR: 2,
  SGD: 2,
  EUR: 2,
  GBP: 2,
  AUD: 2,
  JPY: 0,
};

/**
 * Returns the number of decimal places / exponent for the given currency.
 * Defaults to 0 for IDR and 2 for standard fiat currencies.
 */
export function getCurrencyDecimals(currency: string = "IDR"): number {
  const code = (currency || "IDR").toUpperCase().trim();
  if (code in CURRENCY_DECIMALS) {
    return CURRENCY_DECIMALS[code];
  }
  return 2;
}

/**
 * Validates that an amount is a safe, non-negative integer representing minor units.
 * Throws a descriptive error if the value is NaN, infinite, non-integer, or negative.
 */
export function validateIntegerMoney(
  value: unknown,
  fieldName: string = "Amount",
  allowNegative: boolean = false
): number {
  if (value === null || value === undefined || value === "") {
    throw new Error(`INVALID_PRICE: INVALID_MONEY: ${fieldName} is required.`);
  }

  const num = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(num) || Number.isNaN(num)) {
    throw new Error(`INVALID_PRICE: INVALID_MONEY: ${fieldName} must be a valid finite number.`);
  }

  if (!Number.isInteger(num)) {
    throw new Error(`INVALID_PRICE: INVALID_MONEY: ${fieldName} must be an integer minor unit (received ${num}).`);
  }

  if (!allowNegative && num < 0) {
    throw new Error(`INVALID_PRICE: INVALID_MONEY: ${fieldName} cannot be negative (received ${num}).`);
  }

  return num;
}

/**
 * Converts a major unit decimal amount (e.g. 12.34 USD) to integer minor units (1234).
 */
export function toMinorUnits(amount: number, currency: string = "IDR"): number {
  if (!Number.isFinite(amount) || Number.isNaN(amount)) {
    throw new Error("INVALID_MONEY: Amount must be a valid finite number.");
  }

  const decimals = getCurrencyDecimals(currency);
  const factor = 10 ** decimals;
  const raw = amount * factor;
  const rounded = Math.round(raw);

  // Guard against float precision truncation beyond currency decimals
  if (Math.abs(raw - rounded) > 1e-5) {
    throw new Error(`INVALID_MONEY_PRECISION: Amount ${amount} has more precision than allowed for currency ${currency} (${decimals} decimals).`);
  }

  return rounded;
}

/**
 * Converts integer minor units (e.g. 1234 USD) to major unit number (12.34).
 */
export function fromMinorUnits(minorUnits: number, currency: string = "IDR"): number {
  validateIntegerMoney(minorUnits, "Minor units", true);
  const decimals = getCurrencyDecimals(currency);
  return minorUnits / (10 ** decimals);
}

/**
 * Formats integer minor units into human-readable formatted string.
 */
export function formatMoney(minorUnits: number, currency: string = "IDR", locale?: string): string {
  const code = (currency || "IDR").toUpperCase().trim();
  const decimals = getCurrencyDecimals(code);
  const majorAmount = minorUnits / (10 ** decimals);

  const loc = locale || (code === "IDR" ? "id-ID" : "en-US");

  try {
    return new Intl.NumberFormat(loc, {
      style: "currency",
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(majorAmount);
  } catch {
    return `${code} ${majorAmount.toFixed(decimals)}`;
  }
}
